import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Alert } from '@ovComponents/2-component/alert/alert';
import { Button } from '@ovComponents/2-component/button/button';
import { Form } from '@ovComponents/2-component/form/form';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { SettingsCard } from '@ovComponents/3-pattern/settingsCard/settingsCard';
import { getReconFlagTypeLabel } from '@ovComponents/4-module/reconFlags/utils/reconFlags.config';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import { usePageState } from '@utils/usePageState';
import type { TFunction } from 'i18next';
import type { ChangeEvent } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlagPolicyRowView } from './flagPolicies.types';
import { FlagPolicyRow } from './flagPolicyRow';
import { useFlagPolicyEditor } from './useFlagPolicyEditor';

const filterRows = (
  rows: FlagPolicyRowView[],
  searchText: string,
  t: TFunction,
): FlagPolicyRowView[] => {
  const query = searchText.trim().toLowerCase();
  if (!query) {
    return rows;
  }

  return rows.filter((row) => {
    const label = getReconFlagTypeLabel(t, row.flagType).toLowerCase();
    return label.includes(query) || row.flagType.toLowerCase().includes(query);
  });
};

export const FlagPoliciesSettings = () => {
  const { activeOrganization } = useContext(UserContext);
  const { permissions } = usePermissions();
  const { t } = useTranslation(['settings', 'shared', 'reconciliation']);

  const canRead = permissions.includes('read:flags');
  const canWrite = permissions.includes('write:flags');

  const editor = useFlagPolicyEditor({
    organizationId: activeOrganization?.id,
    canWrite,
  });

  const [searchText, setSearchText] = usePageState('', 'q');

  const filteredRows = useMemo(
    () => filterRows(editor.rows, searchText, t),
    [editor.rows, searchText, t],
  );

  const validationMessage = editor.validationErrorKey
    ? t(editor.validationErrorKey)
    : null;

  const canSubmit =
    canWrite &&
    editor.isOrgSynced &&
    !editor.loading &&
    !editor.saving &&
    editor.isDirty &&
    !editor.validationErrorKey;

  if (!canRead) {
    return (
      <SettingsCard
        title={t('flagPolicies.title')}
        description={t('flagPolicies.description')}
      >
        <Alert severity='warning'>{t('flagPolicies.noAccess')}</Alert>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard
      title={t('flagPolicies.title')}
      description={t('flagPolicies.description')}
    >
      <Form onSubmit={editor.save}>
        <Box display='flex' flexDirection='column' gap={2}>
          <Alert severity='info'>{t('flagPolicies.futureFlagsNotice')}</Alert>

          <TextField
            testId='settings-flag-policies-search'
            label=''
            value={searchText}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setSearchText(event.target.value);
            }}
            leadingIcon='search'
            placeholder={t('flagPolicies.searchPlaceholder')}
          />

          {validationMessage && (
            <Alert severity='error'>{validationMessage}</Alert>
          )}

          {editor.loading && (
            <Typography variant='bodyMedium'>{t('shared:loading')}</Typography>
          )}

          {!editor.loading && filteredRows.length === 0 && (
            <Typography variant='bodyMedium'>
              {t('flagPolicies.noResults')}
            </Typography>
          )}

          {!editor.loading &&
            filteredRows.map((row, index) => (
              <FlagPolicyRow
                key={row.flagType}
                row={row}
                disabled={!canWrite || editor.saving}
                onEnabledChange={editor.setEnabledControlState}
                onPriorityChange={editor.setPriority}
                onThresholdCentsChange={editor.setThresholdCents}
                onPercentThresholdChange={editor.setPercentThreshold}
                testId={`settings-flag-policies-${row.flagType}`}
                underline={index < filteredRows.length - 1}
              />
            ))}

          {canWrite && (
            <Box display='flex' justifyContent='flex-end' pt={1}>
              <Button
                type='submit'
                disabled={!canSubmit}
                loading={editor.saving}
                label={t('shared:save')}
                dataTestId='settings-flag-policies-save'
              />
            </Box>
          )}
        </Box>
      </Form>
    </SettingsCard>
  );
};
